import { useRef } from 'react';
import type { Aircraft, AircraftMeta, FlightTrack, RouteDetail } from '../lib/api';
import { altitudeColor, fmt, tele } from '../lib/altitude';
import ArchiveHistory from './ArchiveHistory';
import FlightHistory from './FlightHistory';

interface AircraftPanelProps {
  aircraft: Aircraft;
  meta: AircraftMeta | null;
  metaLoading: boolean;
  route: RouteDetail | null;
  onClose: () => void;
  watched: boolean;
  onToggleWatch: () => void;
  onReplay: (flightId: number) => void;
  onReplayTrack: (track: FlightTrack) => void;
  /** Eingeklappt = Peek (nur Kopfzeile + Live-Streifen) */
  collapsed: boolean;
  /** Replay läuft → Peek auf Mobile ausblenden (Replay-Leiste trägt den Kontext) */
  replayActive: boolean;
  onToggleCollapse: () => void;
  onExpand: () => void;
  onCollapse: () => void;
}

function Row({
  label,
  value,
  unit,
  color,
  valueClass,
}: {
  label: string;
  value: string;
  unit?: string;
  color?: string;
  valueClass?: string;
}) {
  return (
    <div className="telemetry-row">
      <span className="telemetry-label">{label}</span>
      <span
        className={`telemetry-value mono ${valueClass ?? ''}`}
        style={color ? { color } : undefined}
      >
        {value}
        {unit && <span className="unit">{unit}</span>}
      </span>
    </div>
  );
}

export default function AircraftPanel({
  aircraft,
  meta,
  metaLoading,
  route,
  onClose,
  watched,
  onToggleWatch,
  onReplay,
  onReplayTrack,
  collapsed,
  replayActive,
  onToggleCollapse,
  onExpand,
  onCollapse,
}: AircraftPanelProps) {
  const callsign = aircraft.flight || aircraft.r || aircraft.hex.toUpperCase();
  const registration = aircraft.r ?? meta?.registration ?? '—';
  const typeCode = aircraft.t ?? meta?.type_code ?? '';
  const alt = tele.alt(aircraft.alt_baro);
  const gs = tele.speed(aircraft.gs);
  const vr = tele.vrate(aircraft.baro_rate);
  // Live-Daten vorhanden? (Offline-Watchlist-Flieger haben nur Hex/Label)
  const hasLive =
    aircraft.lat != null ||
    aircraft.alt_baro != null ||
    aircraft.gs != null ||
    aircraft.track != null;

  // Vertikales Wischen am Grabber/Header: hoch → ausklappen, runter → einklappen
  // (bzw. schließen, wenn schon im Peek). Tap bleibt dem onClick überlassen.
  const dragStartY = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    dragStartY.current = e.touches[0]?.clientY ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    const start = dragStartY.current;
    dragStartY.current = null;
    if (start == null) return;
    const dy = (e.changedTouches[0]?.clientY ?? start) - start;
    if (Math.abs(dy) < 30) return; // Tap → onClick
    e.preventDefault(); // synthetischen Click nach Swipe unterdrücken
    if (dy < 0) onExpand();
    else if (collapsed) onClose();
    else onCollapse();
  }

  return (
    <aside
      className={`aircraft-panel ${collapsed ? 'collapsed' : ''} ${replayActive ? 'replay-active' : ''}`}
      aria-label="Flugzeugdetails"
    >
      <button
        className="sheet-grabber"
        onClick={onToggleCollapse}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        aria-label={collapsed ? 'Details ausklappen' : 'Details einklappen'}
      />
      <header onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div>
          <h2 className="mono">{callsign}</h2>
          <p className="subtitle mono">
            {registration}
            {typeCode && ` · ${typeCode}`} · {aircraft.hex.toUpperCase()}
          </p>
        </div>
        <div className="panel-actions">
          <button
            className="collapse-toggle"
            onClick={onToggleCollapse}
            title={collapsed ? 'Details ausklappen' : 'Details einklappen'}
            aria-label="Details ein-/ausklappen"
          >
            {collapsed ? '▴' : '▾'}
          </button>
          <button
            className={`watch-toggle ${watched ? 'active' : ''}`}
            onClick={onToggleWatch}
            title={watched ? 'Von Watchlist entfernen' : 'Zur Watchlist hinzufügen'}
            aria-label="Watchlist umschalten"
          >
            ★
          </button>
          <button className="close" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
      </header>

      {/* Peek: kompakte Echtzeitwerte, ohne die Karte zu verdecken */}
      <div className="live-strip mono" aria-hidden={!collapsed}>
        {hasLive ? (
          <>
            <span className="live-cell">
              <b style={{ color: altitudeColor(aircraft.alt_baro) }}>{alt.value}</b>
              {alt.unit && <i>{alt.unit}</i>}
            </span>
            <span className="live-cell">
              <b>{gs.value}</b>
              {gs.unit && <i>{gs.unit}</i>}
            </span>
            <span className="live-cell">
              <b>{fmt.track(aircraft.track)}</b>
            </span>
            <span className={`live-cell ${vr.dir ? `vr-${vr.dir}` : ''}`}>
              <b>{vr.value}</b>
            </span>
          </>
        ) : (
          <span className="muted">Offline · keine Live-Daten</span>
        )}
      </div>

      {meta?.photo_thumb_url && (
        <img className="aircraft-photo" src={meta.photo_thumb_url} alt={registration} loading="lazy" />
      )}

      <section className="meta">
        {metaLoading ? (
          <>
            <div className="skeleton skeleton-line" style={{ width: '70%' }} />
            <div className="skeleton skeleton-line" style={{ width: '50%' }} />
          </>
        ) : meta ? (
          <>
            {meta.type_name && <p>{meta.manufacturer ? `${meta.manufacturer} ` : ''}{meta.type_name}</p>}
            {meta.operator && <p className="muted">{meta.operator}{meta.country ? ` · ${meta.country}` : ''}</p>}
          </>
        ) : (
          <p className="muted">Keine Stammdaten verfügbar</p>
        )}
        {route && (route.origin || route.destination) && (
          <p
            className="mono route-line"
            title={`${route.origin?.name ?? '?'} → ${route.destination?.name ?? '?'}`}
          >
            {route.origin?.icao ?? '????'} → {route.destination?.icao ?? '????'}
            {route.destination?.municipality ? ` (${route.destination.municipality})` : ''}
          </p>
        )}
      </section>

      {hasLive ? (
        <section className="telemetry">
          <Row label="ALT" value={alt.value} unit={alt.unit} color={altitudeColor(aircraft.alt_baro)} />
          <Row label="GS" value={gs.value} unit={gs.unit} />
          <Row label="TRK" value={fmt.track(aircraft.track)} />
          <Row label="V/R" value={vr.value} unit={vr.unit} valueClass={vr.dir ? `vr-${vr.dir}` : ''} />
          <Row label="SQUAWK" value={aircraft.squawk ?? '—'} />
          <Row label="SIGNAL" value={fmt.seen(aircraft.seen)} />
        </section>
      ) : (
        <p className="muted history-note offline-note">
          Aktuell keine Live-Daten — der Flieger sendet gerade nicht. Historie und Archiv unten.
        </p>
      )}

      <section className="history">
        <h3 className="section-title">FLÜGE (WATCHLIST-TRACKING)</h3>
        <FlightHistory hex={aircraft.hex} onReplay={onReplay} />
      </section>

      <section className="history">
        <h3 className="section-title">ARCHIV (ADSB.LOL)</h3>
        <ArchiveHistory hex={aircraft.hex} onReplayTrack={onReplayTrack} />
      </section>
    </aside>
  );
}
